import { $isAutoLinkNode, $isLinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent } from '@lexical/utils';
import { $getNearestNodeFromDOMNode, $getSelection, $isElementNode, $isRangeSelection, isDOMNode } from 'lexical';

import { useIntegrations } from '../../core/context/IntegrationsContext';
import { useRootEvent } from '../../hooks/useRootEvent';

/**
 * Opens clicked links in a new tab, giving the host's `onLinkClick` a chance to intercept first.
 * - Replaces `@lexical/react`'s `ClickableLinkPlugin`, whose `window.open` bypasses any interception.
 * - The app's outbound-link warning re-clicks the anchor once confirmed; that second pass is let through.
 */
export const ClickableLinkPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const { onLinkClick } = useIntegrations();

  const onClick = (event: MouseEvent) => {
    // Middle click arrives as `auxclick`; other buttons belong to the browser
    if (event.type === 'auxclick' && event.button !== 1) return;
    const target = event.target;
    if (!isDOMNode(target)) return;

    const url = editor.read(() => {
      const node = $getNearestNodeFromDOMNode(target);
      const link = node && $findMatchingParent(node, $isElementNode);
      if (!$isLinkNode(link) || ($isAutoLinkNode(link) && link.getIsUnlinked())) return null;
      return link.sanitizeUrl(link.getURL());
    });
    if (!url) return;

    // Dragging across link text selects it rather than following it
    const selection = editor.read($getSelection);
    if ($isRangeSelection(selection) && !selection.isCollapsed()) {
      event.preventDefault();
      return;
    }

    onLinkClick?.(event);
    if (event.defaultPrevented) return;

    event.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useRootEvent('click', onClick);
  useRootEvent('auxclick', onClick);

  return null;
};
